import React, { createContext, useContext, useMemo } from 'react';
import { useMedia as useEffectMedia } from 'use-media';
import { baseTheme } from '@ui/wrapper';

/*
isMediumAndMore -> from medium size and ALL the above sizes

isLarge -> laptops, desktop, non-mobile devices (or maybe a monster tablet in landscape)

isPrint -> same than above, but matching view to print
(PDF) 
*/
enum DEVICE_TYPE {
  isSmall,
  isMediumAndMore,
  isLarge,
  isXLarge,
  isPrint,
  isXXLarge,
}

export type MediaQueryList = Record<keyof typeof DEVICE_TYPE, boolean>;

const MediaQueryContext = createContext<MediaQueryList>(null!);
MediaQueryContext.displayName = 'MediaQueryContext';

/**
 * Usage:
 * const { isScreen, isLarge } = useMedia()
 *
 * return <p>{isLarge ? 'I am a laptop' : 'I am not'}</p>
 */
const useMedia = () => {
  const context = useContext(MediaQueryContext);

  if (!context) {
    throw new Error(
      'You need a MediaQueryProvider component to expose MediaQueryContext',
    );
  }

  return context;
};

const SIZES = {
  small: `(max-width: 639px)`, // 639 and below
  medium: `(min-width: ${baseTheme.breakpoints[0]})`, // 640 and above
  large: `(min-width: ${baseTheme.breakpoints[2]})`, // 1024 and above
  xlarge: `(min-width: ${baseTheme.breakpoints[3]})`, // 1200 and above
  xxlarge: `(min-width: 1280px)`, // 1280 and above
  print: `print`,
} as const;

// Next queries must be kept in sync with MQ in src/helpers/styles/index.ts
const MediaQueryProvider: React.FC = ({ children }) => {
  const mediaQueries: MediaQueryList = {
    isSmall: useEffectMedia(SIZES.small), // this one covers DEFAULT css in styled-components
    isMediumAndMore: useEffectMedia(SIZES.medium), // >640
    isLarge: useEffectMedia(SIZES.large),
    isXLarge: useEffectMedia(SIZES.xlarge),
    isXXLarge: useEffectMedia(SIZES.xxlarge),
    isPrint: useEffectMedia(SIZES.print),
  };

  const value = useMemo<MediaQueryList>(
    () => ({
      ...mediaQueries,
    }),
    [...Object.values(mediaQueries)],
  );

  return (
    <MediaQueryContext.Provider value={value}>
      {children}
    </MediaQueryContext.Provider>
  );
};

export { useMedia, MediaQueryProvider, SIZES };
